<div class="wizard-shell">
  <mat-card class="wizard-header" [style.background-color]="uiData.individual.color">
    <mat-card-title>
      <mat-icon>{{ uiData.individual.icon }}</mat-icon>
      {{ dialogTitle }}
    </mat-card-title>
  </mat-card>

  <div *ngIf="loading" class="spinner-container">
    <mat-spinner diameter="60" strokeWidth="6"></mat-spinner>
  </div>

  <ng-container *ngIf="!loading && application">
    <mat-card class="status-summary-card" *ngIf="showApplicationStatusSummary">
      <div class="status-summary-grid">
        <div class="status-summary-item">
          <div class="status-summary-label">Application Status</div>
          <div class="status-summary-pill" [ngStyle]="{ '--status-color': sharedService.getStatusColor(displayApplicationStatus) }">
            {{ displayApplicationStatus }}
          </div>
        </div>

        <div class="status-summary-item" *ngIf="applicationStatusDate">
          <div class="status-summary-label">{{ applicationStatusDateLabel }}</div>
          <div class="status-summary-value">{{ applicationStatusDate | date:'MM/dd/yyyy' }}</div>
        </div>
      </div>
    </mat-card>

    <div class="wizard-body">
      <div class="wizard-content">
        <ng-container *ngIf="showWorkflowChooser; else workflowShell">
          <div class="scroll-container chooser-scroll">
            <div class="step-content" fxLayout="column" fxLayoutGap="16px">
              <mat-card class="info-banner">
                <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                  <mat-icon>info</mat-icon>
                  <span>Select how you want to start your Individual application. You can either begin a new application or request to link an existing Individual.</span>
                </div>
              </mat-card>

              <div class="chooser-grid" fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px" fxLayoutAlign="start stretch">
                <div fxFlex="50" fxFlex.lt-md="100">
                  <mat-card class="chooser-card">
                    <div class="chooser-card-body" fxLayout="column" fxLayoutGap="12px">
                      <div class="chooser-card-title">Apply for a New Individual</div>
                      <p>Start a new public Individual application using the same draft-and-submit workflow as Firms.</p>
                      <div fxFlex="1 1 auto"></div>
                      <div fxLayout="row" fxLayoutAlign="center">
                        <button mat-raised-button type="button" color="primary" (click)="selectNewApplication()">
                          <mat-icon>add</mat-icon>
                          New Application
                        </button>
                      </div>
                    </div>
                  </mat-card>
                </div>

                <div fxFlex="50" fxFlex.lt-md="100">
                  <mat-card class="chooser-card">
                    <div class="chooser-card-body" fxLayout="column" fxLayoutGap="12px">
                      <div class="chooser-card-title">Link to an Existing Individual</div>
                      <p>Request access to an already existing Individual through the same application workflow shell.</p>
                      <div fxFlex="1 1 auto"></div>
                      <div fxLayout="row" fxLayoutAlign="center">
                        <button mat-raised-button type="button" color="primary" (click)="selectLinkApplication()">
                          <mat-icon>link</mat-icon>
                          Link Existing Individual
                        </button>
                      </div>
                    </div>
                  </mat-card>
                </div>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-template #workflowShell>
          <mat-stepper [selectedIndex]="currentStep" (selectionChange)="onStepSelectionChange($event)" [orientation]="sharedService.isMobile ? 'vertical' : 'horizontal'" class="firm-stepper">
            <ng-container *ngIf="!isLinkWorkflow; else linkWorkflow">
              <mat-step [hasError]="stepHasValidationErrors(0)">
                <ng-template matStepLabel>Application Information</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <form [formGroup]="applicationForm" class="step-content" fxLayout="column" fxLayoutGap="16px">
                    <mat-card class="info-banner">
                      <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                        <mat-icon>info</mat-icon>
                        <span *ngIf="isRenewalApplication">
                          This renewal starts from an affiliated Individual workspace. The certificate type and renewal target are preselected.
                        </span>
                        <span *ngIf="!isRenewalApplication">
                          Select the certificate type first. You can save a draft from any step before final submission.
                        </span>
                      </div>
                    </mat-card>

                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Application Information</mat-card-title>
                      <mat-card-content>
                        <div fxLayout="column" fxLayoutGap="12px">
                          <mat-form-field>
                            <mat-label>Certificate Type</mat-label>
                            <mat-select formControlName="certificateType" [disabled]="!canEdit || isRenewalApplication">
                              <mat-option *ngFor="let certificateType of getCertificateTypeValues()" [value]="certificateType">{{ certificateType }}</mat-option>
                            </mat-select>
                            <mat-error *ngIf="showApplicationControlError('certificateType', 'required')">Certificate type is required.</mat-error>
                          </mat-form-field>
                        </div>
                      </mat-card-content>
                    </mat-card>
                  </form>
                </div>
              </mat-step>

              <mat-step [hasError]="stepHasValidationErrors(1)">
                <ng-template matStepLabel>Individual Information</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <form [formGroup]="applicationForm" class="step-content" fxLayout="column" fxLayoutGap="16px">
                    <mat-card class="info-banner">
                      <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                        <mat-icon>info</mat-icon>
                        <span>Complete the applicant identity fields before continuing.</span>
                      </div>
                    </mat-card>

                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Individual Information</mat-card-title>
                      <mat-card-content>
                        <div fxLayout="column" fxLayoutGap="12px">
                          <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="8px">
                            <mat-form-field fxFlex="100%">
                              <input matInput placeholder="Title" formControlName="title">
                            </mat-form-field>

                            <mat-form-field fxFlex="100%">
                              <input matInput placeholder="First Name" formControlName="firstName">
                              <mat-error *ngIf="showApplicationControlError('firstName', 'required')">First name is required.</mat-error>
                            </mat-form-field>

                            <mat-form-field fxFlex="100%">
                              <input matInput placeholder="Middle Name" formControlName="middleName">
                            </mat-form-field>

                            <mat-form-field fxFlex="100%">
                              <input matInput placeholder="Last Name" formControlName="lastName">
                              <mat-error *ngIf="showApplicationControlError('lastName', 'required')">Last name is required.</mat-error>
                            </mat-form-field>
                          </div>

                          <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="16px">
                            <div fxFlex="50" fxLayout="column" fxLayoutGap="8px">
                              <span><strong>Social Security Number (Optional)</strong></span>
                              <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="12px" fxLayoutAlign="start center">
                                <mat-form-field fxFlex="100">
                                  <input matInput placeholder="XXX-XX-XXXX" formControlName="ssn">
                                  <mat-error *ngIf="showApplicationControlError('ssn', 'pattern')">Enter a valid SSN.</mat-error>
                                </mat-form-field>
                              </div>
                            </div>

                            <div fxFlex="50" fxLayout="column" fxLayoutGap="8px">
                              <span><strong>Date of Birth</strong></span>
                              <mat-form-field fxFlex="100">
                                <input matInput [matDatepicker]="dobPicker" placeholder="MM/DD/YYYY" formControlName="dateOfBirth" (focus)="dobPicker.open()">
                                <mat-datepicker-toggle matSuffix [for]="dobPicker"></mat-datepicker-toggle>
                                <mat-datepicker #dobPicker></mat-datepicker>
                              </mat-form-field>
                            </div>
                          </div>

                          <mat-checkbox formControlName="isPublic">Check this box if you want to be published on the website.</mat-checkbox>
                        </div>
                      </mat-card-content>
                    </mat-card>
                  </form>
                </div>
              </mat-step>

              <mat-step [hasError]="stepHasValidationErrors(2)">
                <ng-template matStepLabel>Contact Information</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <form [formGroup]="applicationForm" class="step-content" fxLayout="column" fxLayoutGap="16px">
                    <mat-card class="info-banner">
                      <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                        <mat-icon>info</mat-icon>
                        <span>Complete the applicant and representative contact information before continuing.</span>
                      </div>
                    </mat-card>

                    <div class="card-pair-grid">
                      <mat-card class="content-card workflow-card">
                        <mat-card-title>Applicant Contact Information</mat-card-title>
                        <mat-card-content>
                          <div fxLayout="column" fxLayoutGap="12px">
                            <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="8px">
                              <mat-form-field fxFlex="33%">
                                <input matInput placeholder="Primary Phone" formControlName="phone">
                                <mat-error *ngIf="showApplicationControlError('phone', 'required')">Primary phone is required.</mat-error>
                                <mat-error *ngIf="showApplicationControlError('phone', 'pattern')">Enter a valid phone number.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="33%">
                                <input matInput placeholder="Alternate Phone" formControlName="alternatePhone">
                                <mat-error *ngIf="showApplicationControlError('alternatePhone', 'pattern')">Enter a valid phone number.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="33%">
                                <input matInput placeholder="Fax" formControlName="faxPhone">
                                <mat-error *ngIf="showApplicationControlError('faxPhone', 'pattern')">Enter a valid phone number.</mat-error>
                              </mat-form-field>
                            </div>

                            <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="8px">
                              <mat-form-field fxFlex="50%">
                                <input matInput placeholder="Email" formControlName="email">
                                <mat-error *ngIf="showApplicationControlError('email', 'email')">Enter a valid email address.</mat-error>
                                <mat-error *ngIf="showApplicationControlError('email', 'emailMismatch')">Emails must match.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="50%">
                                <input matInput placeholder="Confirm Email" formControlName="confirmEmail">
                                <mat-error *ngIf="showApplicationControlError('confirmEmail', 'required')">Confirm email is required.</mat-error>
                                <mat-error *ngIf="showApplicationControlError('confirmEmail', 'email')">Enter a valid email address.</mat-error>
                                <mat-error *ngIf="showApplicationControlError('confirmEmail', 'emailMismatch')">Emails must match.</mat-error>
                              </mat-form-field>
                            </div>

                            <mat-form-field>
                              <input matInput placeholder="Website" formControlName="website">
                            </mat-form-field>
                          </div>
                        </mat-card-content>
                      </mat-card>

                      <mat-card class="content-card workflow-card">
                        <mat-card-title>Representative Contact Information</mat-card-title>
                        <mat-card-content>
                          <div fxLayout="column" fxLayoutGap="12px">
                            <mat-form-field>
                              <input matInput placeholder="Representative Name / Label" formControlName="repLabel">
                            </mat-form-field>

                            <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="8px">
                              <mat-form-field fxFlex="33%">
                                <input matInput placeholder="Primary Phone" formControlName="repPhone">
                                <mat-error *ngIf="showApplicationControlError('repPhone', 'pattern')">Enter a valid phone number.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="33%">
                                <input matInput placeholder="Alternate Phone" formControlName="repAlternatePhone">
                                <mat-error *ngIf="showApplicationControlError('repAlternatePhone', 'pattern')">Enter a valid phone number.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="33%">
                                <input matInput placeholder="Fax" formControlName="repFaxPhone">
                                <mat-error *ngIf="showApplicationControlError('repFaxPhone', 'pattern')">Enter a valid phone number.</mat-error>
                              </mat-form-field>
                            </div>

                            <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="8px">
                              <mat-form-field fxFlex="50%">
                                <input matInput placeholder="Email" formControlName="repEmail">
                                <mat-error *ngIf="showApplicationControlError('repEmail', 'email')">Enter a valid email address.</mat-error>
                                <mat-error *ngIf="showApplicationControlError('repEmail', 'emailMismatch')">Emails must match.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="50%">
                                <input matInput placeholder="Confirm Email" formControlName="repConfirmEmail">
                                <mat-error *ngIf="showApplicationControlError('repConfirmEmail', 'email')">Enter a valid email address.</mat-error>
                                <mat-error *ngIf="showApplicationControlError('repConfirmEmail', 'emailMismatch')">Emails must match.</mat-error>
                              </mat-form-field>
                            </div>

                            <mat-form-field>
                              <input matInput placeholder="Website" formControlName="repWebsite">
                            </mat-form-field>
                          </div>
                        </mat-card-content>
                      </mat-card>
                    </div>
                  </form>
                </div>
              </mat-step>

              <mat-step [hasError]="stepHasValidationErrors(3)">
                <ng-template matStepLabel>Mailing Address</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <form [formGroup]="applicationForm" class="step-content" fxLayout="column" fxLayoutGap="16px">
                    <mat-card class="info-banner">
                      <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                        <mat-icon>info</mat-icon>
                        <span>Provide the mailing address for this application.</span>
                      </div>
                    </mat-card>

                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Mailing Address</mat-card-title>
                      <mat-card-content>
                        <div class="address-block">
                          <div class="address-grid-primary">
                            <mat-form-field>
                              <mat-label>Street Apt/Unit/Suite</mat-label>
                              <input matInput formControlName="mailingStreet">
                              <mat-error *ngIf="showMailingRequiredError('mailingStreet')">Street address is required.</mat-error>
                            </mat-form-field>

                            <mat-form-field>
                              <mat-label>City</mat-label>
                              <input matInput formControlName="mailingCity">
                              <mat-error *ngIf="showMailingRequiredError('mailingCity')">City is required.</mat-error>
                            </mat-form-field>
                          </div>

                          <div class="address-grid-secondary" [class.with-county]="showMailingCounty">
                            <mat-form-field>
                              <mat-label>State</mat-label>
                              <mat-select formControlName="mailingState" (selectionChange)="onStateSelectionChange()">
                                <mat-option *ngFor="let state of sharedService.states" [value]="state">{{ state }}</mat-option>
                              </mat-select>
                              <mat-error *ngIf="showMailingRequiredError('mailingState')">State is required.</mat-error>
                            </mat-form-field>

                            <mat-form-field>
                              <mat-label>ZIP</mat-label>
                              <input matInput formControlName="mailingZip">
                              <mat-error *ngIf="showMailingRequiredError('mailingZip')">ZIP code is required.</mat-error>
                              <mat-error *ngIf="showApplicationControlError('mailingZip', 'pattern')">Enter a valid ZIP code.</mat-error>
                            </mat-form-field>

                            <mat-form-field *ngIf="showMailingCounty">
                              <mat-label>County</mat-label>
                              <mat-select formControlName="mailingCounty">
                                <mat-option [value]="''">Select County</mat-option>
                                <mat-option *ngFor="let county of sharedService.counties" [value]="county">{{ county }}</mat-option>
                              </mat-select>
                            </mat-form-field>
                          </div>
                        </div>
                      </mat-card-content>
                    </mat-card>
                  </form>
                </div>
              </mat-step>

              <mat-step>
                <ng-template matStepLabel>Documents</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <div class="step-content" fxLayout="column" fxLayoutGap="16px">
                    <mat-card class="info-banner">
                      <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                        <mat-icon>info</mat-icon>
                        <span>Upload supporting documents for the application. Documents can no longer be removed once the application is submitted.</span>
                      </div>
                    </mat-card>

                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Documents</mat-card-title>
                      <mat-card-content>
                        <div class="table-toolbar">
                          <div class="table-summary">Results ({{ filteredDocuments.length }})</div>
                          <mat-form-field class="table-filter-field">
                            <mat-label>Filter Documents</mat-label>
                            <input matInput [(ngModel)]="documentsFilterString" [ngModelOptions]="{ standalone: true }" (input)="filterDocumentTable()">
                          </mat-form-field>
                          <button mat-raised-button color="primary" type="button" *ngIf="canEdit" (click)="openDocumentUploadDialog()">
                            <mat-icon>upload_file</mat-icon>
                            Upload Document
                          </button>
                        </div>

                        <div *ngIf="!filteredDocuments.length" class="empty-state small">No documents uploaded yet.</div>

                        <mat-table *ngIf="filteredDocuments.length" [dataSource]="filteredDocuments" class="mat-elevation-z8 step-table">
                          <ng-container matColumnDef="fileName">
                            <mat-header-cell *matHeaderCellDef>File Name</mat-header-cell>
                            <mat-cell *matCellDef="let row">{{ row.fileName }}</mat-cell>
                          </ng-container>

                          <ng-container matColumnDef="fileDescription">
                            <mat-header-cell *matHeaderCellDef>Description</mat-header-cell>
                            <mat-cell *matCellDef="let row">{{ row.fileDescription || reviewPlaceholder }}</mat-cell>
                          </ng-container>

                          <ng-container matColumnDef="actions">
                            <mat-header-cell *matHeaderCellDef>Actions</mat-header-cell>
                            <mat-cell *matCellDef="let row">
                              <div class="action-buttons compact">
                                <button mat-button type="button" color="primary" (click)="downloadDocument(row)">Download</button>
                                <button
                                  mat-button
                                  type="button"
                                  color="warn"
                                  *ngIf="canDeleteDocuments"
                                  (click)="deleteDocument(row)">Delete</button>
                              </div>
                            </mat-cell>
                          </ng-container>

                          <mat-header-row fxLayout="row" *matHeaderRowDef="['fileName', 'fileDescription', 'actions']"></mat-header-row>
                          <mat-row fxLayout="row" *matRowDef="let row; columns: ['fileName', 'fileDescription', 'actions']"></mat-row>
                        </mat-table>
                      </mat-card-content>
                    </mat-card>
                  </div>
                </div>
              </mat-step>

              <mat-step [hasError]="stepHasValidationErrors(5)">
                <ng-template matStepLabel>Review &amp; Submit</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <div class="step-content" fxLayout="column" fxLayoutGap="16px">
                    <mat-card class="content-card workflow-card">
                      <mat-card-title>Review &amp; Submit</mat-card-title>
                      <mat-card-content>
                        <mat-accordion class="review-accordion" multi>
                          <mat-expansion-panel class="review-panel" [expanded]="true">
                            <mat-expansion-panel-header>
                              <mat-panel-title>Application Information</mat-panel-title>
                            </mat-expansion-panel-header>
                            <div class="review-grid">
                              <div class="review-field">
                                <div class="review-label">Certificate Type</div>
                                <div class="review-value">{{ getCertificateTypeLabel(applicationForm.get('certificateType')?.value) }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">Renewal</div>
                                <div class="review-value">{{ applicationForm.get('renewal')?.value ? 'Yes' : 'No' }}</div>
                              </div>
                              <div class="review-field full-width" *ngIf="applicationForm.get('renewal')?.value">
                                <div class="review-label">Previous Application / Control Number</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('previousApplication')?.value) }}</div>
                              </div>
                            </div>
                          </mat-expansion-panel>

                          <mat-expansion-panel class="review-panel" [expanded]="true">
                            <mat-expansion-panel-header>
                              <mat-panel-title>Individual Information</mat-panel-title>
                            </mat-expansion-panel-header>
                            <div class="review-grid">
                              <div class="review-field">
                                <div class="review-label">Title</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('title')?.value) }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">First Name</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('firstName')?.value) }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">Middle Name</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('middleName')?.value) }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">Last Name</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('lastName')?.value) }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">Social Security Number</div>
                                <div class="review-value">{{ applicationForm.get('ssn')?.value ? getReviewValue(applicationForm.get('ssn')?.value) : 'Exempt' }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">Date of Birth</div>
                                <div class="review-value">{{ applicationForm.get('dateOfBirth')?.value ? (applicationForm.get('dateOfBirth')?.value | date:'MM/dd/yyyy') : reviewPlaceholder }}</div>
                              </div>
                              <div class="review-field full-width">
                                <div class="review-label">Public Listing</div>
                                <div class="review-value">{{ applicationForm.get('isPublic')?.value ? 'Publish on website' : 'Do not publish' }}</div>
                              </div>
                            </div>
                          </mat-expansion-panel>

                          <mat-expansion-panel class="review-panel" [expanded]="true">
                            <mat-expansion-panel-header>
                              <mat-panel-title>Contact Information</mat-panel-title>
                            </mat-expansion-panel-header>
                            <div class="review-subsection-grid">
                              <div class="review-subsection-card">
                                <div class="review-subsection-title">Applicant Contact Information</div>
                                <div class="summary-grid">
                                  <div><strong>Primary Phone:</strong> {{ getReviewValue(applicationForm.get('phone')?.value) }}</div>
                                  <div><strong>Alternate Phone:</strong> {{ getReviewValue(applicationForm.get('alternatePhone')?.value) }}</div>
                                  <div><strong>Fax:</strong> {{ getReviewValue(applicationForm.get('faxPhone')?.value) }}</div>
                                  <div><strong>Email:</strong> {{ getReviewValue(applicationForm.get('email')?.value) }}</div>
                                  <div><strong>Website:</strong> {{ getReviewValue(applicationForm.get('website')?.value) }}</div>
                                </div>
                              </div>

                              <div class="review-subsection-card">
                                <div class="review-subsection-title">Representative Contact Information</div>
                                <div class="summary-grid">
                                  <div><strong>Name:</strong> {{ getReviewValue(applicationForm.get('repLabel')?.value) }}</div>
                                  <div><strong>Primary Phone:</strong> {{ getReviewValue(applicationForm.get('repPhone')?.value) }}</div>
                                  <div><strong>Alternate Phone:</strong> {{ getReviewValue(applicationForm.get('repAlternatePhone')?.value) }}</div>
                                  <div><strong>Fax:</strong> {{ getReviewValue(applicationForm.get('repFaxPhone')?.value) }}</div>
                                  <div><strong>Email:</strong> {{ getReviewValue(applicationForm.get('repEmail')?.value) }}</div>
                                  <div><strong>Website:</strong> {{ getReviewValue(applicationForm.get('repWebsite')?.value) }}</div>
                                </div>
                              </div>
                            </div>
                          </mat-expansion-panel>

                          <mat-expansion-panel class="review-panel" [expanded]="true">
                            <mat-expansion-panel-header>
                              <mat-panel-title>Mailing Address</mat-panel-title>
                            </mat-expansion-panel-header>
                            <div class="review-grid">
                              <div class="review-field full-width">
                                <div class="review-label">Street</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('mailingStreet')?.value) }}</div>
                              </div>
                              <div class="review-field full-width">
                                <div class="review-label">City</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('mailingCity')?.value) }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">State</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('mailingState')?.value) }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">ZIP</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('mailingZip')?.value) }}</div>
                              </div>
                              <div class="review-field">
                                <div class="review-label">County</div>
                                <div class="review-value">{{ getReviewValue(applicationForm.get('mailingCounty')?.value) }}</div>
                              </div>
                            </div>
                          </mat-expansion-panel>

                          <mat-expansion-panel class="review-panel" [expanded]="true">
                            <mat-expansion-panel-header>
                              <mat-panel-title>Documents</mat-panel-title>
                            </mat-expansion-panel-header>
                            <div *ngIf="!documents.length" class="review-empty-state">{{ reviewPlaceholder }}</div>
                            <mat-table *ngIf="documents.length" [dataSource]="documents" class="mat-elevation-z8 step-table review-table">
                              <ng-container matColumnDef="fileName">
                                <mat-header-cell *matHeaderCellDef>File Name</mat-header-cell>
                                <mat-cell *matCellDef="let row">{{ row.fileName }}</mat-cell>
                              </ng-container>
                              <ng-container matColumnDef="fileDescription">
                                <mat-header-cell *matHeaderCellDef>Description</mat-header-cell>
                                <mat-cell *matCellDef="let row">{{ row.fileDescription || reviewPlaceholder }}</mat-cell>
                              </ng-container>
                              <mat-header-row fxLayout="row" *matHeaderRowDef="['fileName', 'fileDescription']"></mat-header-row>
                              <mat-row fxLayout="row" *matRowDef="let row; columns: ['fileName', 'fileDescription']"></mat-row>
                            </mat-table>
                          </mat-expansion-panel>
                        </mat-accordion>

                        <form [formGroup]="attestationForm">
                          <mat-card class="content-card">
                            <mat-card-title>Signature and Acknowledgments</mat-card-title>
                            <mat-card-content>
                              <div fxLayout="column" fxLayoutGap="12px">
                                <mat-checkbox formControlName="attestation">I understand and agree with the information provided in this application.</mat-checkbox>
                                <mat-error *ngIf="showAttestationError('attestation', 'requiredTrue')">You must attest before submitting.</mat-error>

                                <mat-form-field>
                                  <mat-label>Name or Initials</mat-label>
                                  <input matInput formControlName="esignature">
                                  <mat-error *ngIf="showAttestationError('esignature', 'required')">Electronic signature is required.</mat-error>
                                </mat-form-field>
                              </div>
                            </mat-card-content>
                          </mat-card>
                        </form>
                      </mat-card-content>
                    </mat-card>
                  </div>
                </div>
              </mat-step>
            </ng-container>

            <ng-template #linkWorkflow>
              <mat-step [hasError]="stepHasValidationErrors(0)">
                <ng-template matStepLabel>Individual Information</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <div class="step-content" fxLayout="column" fxLayoutGap="16px">
                    <mat-card class="info-banner">
                      <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                        <mat-icon class="link-step-icon">info</mat-icon>
                        <span class="link-step-banner-text">Provide the existing Individual lookup information below. First name and last name are required, plus at least one of Date of Birth, Phone Number, or Email. You may provide all three.</span>
                      </div>
                    </mat-card>

                    <form [formGroup]="applicationForm" class="link-step-form">
                      <mat-card class="content-card workflow-card link-lookup-card">
                        <mat-card-title>Individual Lookup</mat-card-title>
                        <mat-card-content>
                          <div fxLayout="column" fxLayoutGap="12px">
                            <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="8px">
                              <mat-form-field fxFlex="50%">
                                <mat-label>First Name</mat-label>
                                <input matInput formControlName="firstName" (input)="onLinkLookupInputChange()">
                                <mat-error *ngIf="showApplicationControlError('firstName', 'required')">First name is required.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="50%">
                                <mat-label>Last Name</mat-label>
                                <input matInput formControlName="lastName" (input)="onLinkLookupInputChange()">
                                <mat-error *ngIf="showApplicationControlError('lastName', 'required')">Last name is required.</mat-error>
                              </mat-form-field>
                            </div>

                            <div class="link-lookup-note">
                              Please enter at least one of the following: Date of Birth, Phone Number, or Email.
                            </div>

                            <div class="link-lookup-note">
                              Matching checks run when you continue to the next step or submit the request.
                            </div>

                            <div fxLayout="row" fxLayout.lt-md="column" fxLayoutGap="8px">
                              <mat-form-field fxFlex="33%">
                                <mat-label>Email</mat-label>
                                <input matInput formControlName="linkLookupEmail" (input)="onLinkLookupInputChange()">
                                <mat-error *ngIf="showApplicationControlError('linkLookupEmail', 'email')">Enter a valid email address.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="33%">
                                <mat-label>Phone Number</mat-label>
                                <input matInput formControlName="linkLookupPhone" (input)="onLinkLookupInputChange()">
                                <mat-error *ngIf="showApplicationControlError('linkLookupPhone', 'pattern')">Enter a valid phone number.</mat-error>
                              </mat-form-field>

                              <mat-form-field fxFlex="33%">
                                <mat-label>Date of Birth</mat-label>
                                <input matInput [matDatepicker]="linkDobPicker" formControlName="dateOfBirth" placeholder="MM/DD/YYYY" (dateChange)="onLinkLookupInputChange()" (focus)="linkDobPicker.open()">
                                <mat-datepicker-toggle matSuffix [for]="linkDobPicker"></mat-datepicker-toggle>
                                <mat-datepicker #linkDobPicker></mat-datepicker>
                              </mat-form-field>
                            </div>

                            <div class="field-error-text" *ngIf="validationSummaryRequested && !hasLinkLookupSupplementalValue()">
                              Enter at least one of Date of Birth, Phone Number, or Email.
                            </div>

                            <div class="link-lookup-status" *ngIf="linkLookupLoading">
                              <mat-spinner diameter="28" strokeWidth="4"></mat-spinner>
                              <span>Checking submitted lookup information...</span>
                            </div>
                          </div>
                        </mat-card-content>
                      </mat-card>
                    </form>
                  </div>
                </div>
              </mat-step>

              <mat-step [hasError]="stepHasValidationErrors(1)">
                <ng-template matStepLabel>Signature and Acknowledgments</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <div style="padding: 16px;">
                    <div fxLayout="column" fxLayoutGap="16px">
                      <mat-card class="warning-banner">
                        <div fxLayout="row" fxLayoutAlign="start start" fxLayoutGap="12px">
                          <mat-icon style="color: #e65100; flex-shrink: 0;">info</mat-icon>
                          <span style="color: #e65100; font-size: 14px; line-height: 1.5;">
                            Please verify that the information below is correct.
                          </span>
                        </div>
                      </mat-card>

                      <div class="review-grid">
                        <div class="review-field">
                          <span class="review-label">First Name</span>
                          <span class="review-value">{{ getReviewValue(applicationForm.get('firstName')?.value) }}</span>
                        </div>
                        <div class="review-field">
                          <span class="review-label">Last Name</span>
                          <span class="review-value">{{ getReviewValue(applicationForm.get('lastName')?.value) }}</span>
                        </div>
                        <div class="review-field">
                          <span class="review-label">Phone Number</span>
                          <span class="review-value">{{ getReviewValue(applicationForm.get('linkLookupPhone')?.value) }}</span>
                        </div>
                        <div class="review-field">
                          <span class="review-label">Email</span>
                          <span class="review-value">{{ getReviewValue(applicationForm.get('linkLookupEmail')?.value) }}</span>
                        </div>
                        <div class="review-field full-width">
                          <span class="review-label">Date of Birth</span>
                          <span class="review-value">{{ applicationForm.get('dateOfBirth')?.value ? (applicationForm.get('dateOfBirth')?.value | date:'MM/dd/yyyy') : reviewPlaceholder }}</span>
                        </div>
                      </div>

                      <p *ngIf="hasUniqueLinkMatch && showMatchedLinkApprovalEmail">
                        A unique matching Individual was identified. The individual will be emailed at
                        <a [href]="'mailto:' + matchedLinkApprovalEmail" style="color: #1976d2;"><strong>{{ matchedLinkApprovalEmail }}</strong></a>.
                      </p>
                      <p *ngIf="hasUniqueLinkMatch && !showMatchedLinkApprovalEmail">
                        A unique matching Individual was identified. The individual will be emailed.
                      </p>
                      <p *ngIf="hasMultipleLinkMatches">
                        Multiple matching Individuals were identified. This request will be sent to
                        <a [href]="'mailto:' + approvalEmail" style="color: #1976d2;"><strong>{{ approvalEmail }}</strong></a>
                        for staff review.
                      </p>
                      <p *ngIf="hasNoLinkMatch">
                        No matching Individual was identified. This request will be sent to
                        <a [href]="'mailto:' + approvalEmail" style="color: #1976d2;"><strong>{{ approvalEmail }}</strong></a>
                        for staff review.
                      </p>

                      <form [formGroup]="attestationForm">
                        <div fxLayout="column" fxLayoutGap="12px">
                          <mat-checkbox formControlName="attestation" required>
                            I attest that I am authorized/affiliated to manage the individual that matches the submitted information.
                          </mat-checkbox>
                          <div class="field-error-text" *ngIf="showAttestationError('attestation', 'requiredTrue')">You must attest before submitting.</div>

                          <mat-form-field appearance="fill">
                            <mat-label>E-Signature</mat-label>
                            <input matInput formControlName="esignature">
                            <mat-error *ngIf="showAttestationError('esignature', 'required')">E-Signature is required.</mat-error>
                          </mat-form-field>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </mat-step>

              <mat-step *ngIf="isSubmitted">
                <ng-template matStepLabel>Confirmation</ng-template>

                <div class="scroll-container" [class.has-feedback]="hasFeedbackContent()">
                  <div fxLayout="column" fxLayoutAlign="center center" style="padding: 40px; text-align: center;">
                    <mat-card style="padding: 30px; max-width: 600px;">
                      <mat-card-content style="display: flex; flex-direction: column; align-items: center;">
                        <mat-icon style="font-size: 64px; height: 64px; width: 64px; color: #4caf50; margin-bottom: 20px;">check_circle</mat-icon>
                        <h2 style="color: #4caf50; margin-bottom: 20px;">Link Request Submitted Successfully!</h2>
                        <p style="font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
                          Your link request has been submitted and is being reviewed.
                        </p>
                        <div style="padding: 16px; border-radius: 4px; margin-bottom: 20px; width: 100%;">
                          <p style="margin: 4px 0;" *ngIf="hasUniqueLinkMatch && showMatchedLinkApprovalEmail">
                            An approval email has been sent to:
                            <a [href]="'mailto:' + matchedLinkApprovalEmail" style="color: #1976d2;"><strong>{{ matchedLinkApprovalEmail }}</strong></a>
                          </p>
                          <p style="margin: 4px 0;" *ngIf="hasUniqueLinkMatch && !showMatchedLinkApprovalEmail">
                            A unique matching Individual was identified and the individual has been emailed.
                          </p>
                          <p style="margin: 4px 0;" *ngIf="hasMultipleLinkMatches">
                            Multiple matching Individuals were identified. The request has been sent to
                            <a [href]="'mailto:' + approvalEmail" style="color: #1976d2;"><strong>{{ approvalEmail }}</strong></a>
                            for staff review.
                          </p>
                          <p style="margin: 4px 0;" *ngIf="hasNoLinkMatch">
                            No matching Individual was identified. The request has been sent to
                            <a [href]="'mailto:' + approvalEmail" style="color: #1976d2;"><strong>{{ approvalEmail }}</strong></a>
                            for staff review.
                          </p>
                        </div>
                        <p style="font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
                          If you have any questions or concerns, please contact us at:
                        </p>
                        <div style="padding: 16px; border-radius: 4px; margin-bottom: 20px;">
                          <p style="margin: 4px 0;"><strong>Email: </strong>
                            <a [href]="'mailto:' + approvalEmail" style="color: #1976d2;">{{ approvalEmail }}</a>
                          </p>
                          <p style="margin: 4px 0;"><strong>Phone: </strong>
                            <a href="tel:971-673-0440" style="color: #1976d2;">971-673-0440</a>
                          </p>
                        </div>
                      </mat-card-content>
                    </mat-card>
                  </div>
                </div>
              </mat-step>
            </ng-template>
          </mat-stepper>
        </ng-template>

        <div *ngIf="hasFeedbackContent()" class="validation-summary" [class.collapsed]="isFeedbackCollapsed">
          <button
            type="button"
            class="validation-summary-header validation-summary-toggle"
            (click)="toggleFeedbackPanel()"
            [attr.aria-expanded]="!isFeedbackCollapsed"
            [attr.aria-label]="isFeedbackCollapsed ? 'Expand deficiencies panel' : 'Minimize deficiencies panel'">
            <span class="validation-summary-title">
              <mat-icon>warning</mat-icon>
              <span>Application Feedback / Deficiencies</span>
            </span>
            <span class="validation-summary-action">{{ isFeedbackCollapsed ? 'Expand' : 'Minimize' }}</span>
          </button>
          <div *ngIf="!isFeedbackCollapsed" #validationSummaryCard class="validation-card">
            <ng-container *ngIf="getPersistedDeficiencyDescriptions() as persistedDeficiencies">
              <div class="validation-feedback-section" *ngIf="persistedDeficiencies.length > 0">
                <div class="feedback-text" *ngFor="let deficiencyDescription of persistedDeficiencies">
                  {{ deficiencyDescription }}
                </div>
              </div>
            </ng-container>

            <ng-container *ngIf="getValidationFeedbackGroups() as validationGroups">
              <div class="validation-feedback-section" *ngIf="validationGroups.length > 0">
                <div class="validation-feedback-heading">Missing Required Fields</div>
                <div class="validation-feedback-group" *ngFor="let validationGroup of validationGroups">
                  <div class="validation-feedback-step">{{ validationGroup.step }}</div>
                  <div class="validation-feedback-links">
                    <button
                      type="button"
                      class="validation-feedback-link"
                      *ngFor="let validationError of validationGroup.errors"
                      (click)="focusValidationFeedbackError(validationError)">
                      {{ validationError.label }}
                    </button>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>
        </div>
      </div>
    </div>

    <mat-card class="action-card" [style.background-color]="uiData.individual.color" fxLayoutGap="8px">
      <button mat-raised-button type="button" (click)="close()" [disabled]="saving">{{ canEdit ? 'Close' : 'Exit' }}</button>
      <button mat-raised-button type="button" color="warn" *ngIf="showCancelButton()" (click)="cancelApplication()" [disabled]="isCancelButtonDisabled()">{{ getCancelButtonText() }}</button>
      <button mat-raised-button type="button" color="primary" *ngIf="!showWorkflowChooser && !isLinkWorkflow" (click)="saveDraft()" [disabled]="!canEdit || saving">Save</button>
      <div fxFlex="1 1 auto"></div>
      <button *ngIf="showPreviousButton()" mat-raised-button type="button" (click)="goToPreviousStep()" [disabled]="saving">Previous</button>
      <button *ngIf="showNextButton()" mat-raised-button type="button" color="primary" (click)="goToNextStep()" [disabled]="saving">Next</button>
      <button *ngIf="showSubmitButton()" mat-raised-button type="button" color="primary" (click)="submitApplication()" [disabled]="isSubmitButtonDisabled()">{{ isLinkWorkflow ? 'Submit Link Request' : 'Submit Application' }}</button>
    </mat-card>
  </ng-container>
</div>